const SHEET_ID = import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL = import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID = import.meta.env.VITE_CHAT_ID;
const BASE_URL = `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}`;
let cachedToken = { token: null, expiresAt: 0 };
let sheetMeta = null;

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

async function requestWithRetry(url, options, attempt = 0) {
  const response = await fetch(url, options);
  if (response.status === 429 && attempt < 3) {
    await wait(500 * (2 ** attempt));
    return requestWithRetry(url, options, attempt + 1);
  }
  return response;
}

export async function getAccessToken() {
  if (cachedToken.token && Date.now() < cachedToken.expiresAt) {
    return cachedToken.token;
  }

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chatId: CHAT_ID }),
  });
  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unable to authorize Google Sheets access');
  }

  cachedToken = {
    token: data.access_token,
    expiresAt: Date.now() + Math.max(30, data.expires_in - 60) * 1000,
  };
  return cachedToken.token;
}

export async function sheetsRequest(path, options = {}) {
  const token = await getAccessToken();
  const response = await requestWithRetry(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Google Sheets API ${response.status}: ${await response.text()}`);
  }
  if (response.status === 204) return {};

  const text = await response.text();
  return text ? JSON.parse(text) : {};
}

export async function getRows(range) {
  const result = await sheetsRequest(`/values/${encodeURIComponent(range)}`);
  return result.values || [];
}

export async function appendRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      body: JSON.stringify({ values: [values] }),
    },
  );
}

export async function updateRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      body: JSON.stringify({ values: [values] }),
    },
  );
}

export async function getSheetMeta(force = false) {
  if (!force && sheetMeta) return sheetMeta;

  const data = await sheetsRequest('/?fields=sheets.properties');
  sheetMeta = Object.fromEntries(
    (data.sheets || []).map((sheet) => [
      sheet.properties.title,
      sheet.properties.sheetId,
    ]),
  );
  return sheetMeta;
}

export async function getSheetIdByTitle(title) {
  const metadata = await getSheetMeta();
  if (metadata[title] !== undefined) return metadata[title];

  const refreshedMetadata = await getSheetMeta(true);
  return refreshedMetadata[title];
}

function columnLetter(number) {
  let result = '';
  while (number > 0) {
    const remainder = (number - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    number = Math.floor((number - 1) / 26);
  }
  return result;
}

export async function ensureTab(title, headers) {
  let metadata = await getSheetMeta();

  if (metadata[title] === undefined) {
    await sheetsRequest(':batchUpdate', {
      method: 'POST',
      body: JSON.stringify({
        requests: [{ addSheet: { properties: { title } } }],
      }),
    });
    sheetMeta = null;
    metadata = await getSheetMeta(true);
  }

  const lastColumn = columnLetter(headers.length);
  const headerRows = await getRows(`${title}!A1:${lastColumn}1`);
  if (!headerRows[0] || headerRows[0].length === 0) {
    await updateRow(`${title}!A1:${lastColumn}1`, headers);
  }
}

export async function deleteRow(title, id) {
  const sheetId = await getSheetIdByTitle(title);
  if (sheetId === undefined) {
    throw new Error(`Sheet tab not found: ${title}`);
  }

  const ids = await getRows(`${title}!A:A`);
  const rowIndex = ids.findIndex(
    (row, index) => index > 0 && String(row?.[0] || '') === String(id),
  );
  if (rowIndex < 0) {
    throw new Error(`Record not found: ${id}`);
  }

  const result = await sheetsRequest(':batchUpdate', {
    method: 'POST',
    body: JSON.stringify({
      requests: [{
        deleteDimension: {
          range: {
            sheetId: Number(sheetId),
            dimension: 'ROWS',
            startIndex: rowIndex,
            endIndex: rowIndex + 1,
          },
        },
      }],
    }),
  });

  sheetMeta = null;
  return result;
}